// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Reviews from 'brastrap/promotional/reviews-container/ReviewsContainer';
import {
  filter,
  apiFetchReviews,
  apiFetchAllReviews,
} from '../../../actions/product-reviews';
import { type State as ReviewsStateProps } from '../../../reducers/product-reviews';

const ALL_REVIEWS_CODE = 'all';
const DEFAULT_LIMIT = 8;
const DEFAULT_MOBILE_LIMIT = 8;
const MAX_RATING_VALUE = 3;

type DispatchProps = {
  apiFetchReviews: typeof apiFetchReviews,
  apiFetchAllReviews: typeof apiFetchAllReviews,
  filter: typeof filter,
};

type OtherProps = {
  isMobile: boolean,
  rating: Rating,
  styleCode: string,
  isYotpoEnabled: boolean,
  isAllReviews: boolean,
};

type Props = DispatchProps & ReviewsStateProps & OtherProps;

class ProductReviews extends Component<Props> {
  /**
   * On initial render, fetch all reviews
   */
  componentDidMount() {
    const limit = this.props.limit;
    const page = this.props.page;
    const sortBy = this.props.sortBy;
    const starRating = this.props.starRating;

    const filters = { limit, page, sortBy, starRating };

    if (this.props.isAllReviews) {
      this.props.apiFetchAllReviews(filters);
    }
  }

  /**
   * Check if we need to make a new request for reviews
   */
  componentWillReceiveProps({ sortBy, page, limit, starRating }) {
    if (
      sortBy !== this.props.sortBy ||
      page !== this.props.page ||
      limit !== this.props.limit ||
      starRating !== this.props.starRating
    ) {
      const id = sortBy === ALL_REVIEWS_CODE ? this.props.styleCode : sortBy;
      const filters = { limit, page, sortBy, starRating };

      if (!this.props.isAllReviews) {
        this.props.apiFetchReviews(id, filters);
      } else {
        this.props.apiFetchAllReviews(filters);
      }
    }
  }

  /**
   * Get only the currently visible reviews to pass on
   * as props to the reviews component.
   * @return {Object} - props
   */
  filterReviews() {
    const { limit, page } = this.props;
    const reviews = Array.isArray(this.props.reviews) ? this.props.reviews : [];
    const start = limit * (page - 1);
    const end = start + limit;
    return {
      ...this.props,
      reviews: reviews.length <= limit ? reviews : reviews.slice(start, end),
    };
  }

  /**
   * You know what this does.
   * @return {*}
   */
  render() {
    return (
      <Reviews
        {...this.filterReviews()}
        isAllReviews={this.props.isAllReviews}
      />
    );
  }
}

/**
 * Only show the ratings for the selected style colour.
 *  If yotpo integration is enabled just return the style rating
 *   As we don't have individual style colour ratings in yotpo
 * @param {Object} ratings
 * @param {String} styleColourCode
 * @param {Boolean} isYotpoEnabled
 * @returns {{fit: null, style: null, total: number, trueToSize: null, value: null}}
 */
const mapRating = (ratings, styleColourCode, isYotpoEnabled = false) => {
  if (styleColourCode === ALL_REVIEWS_CODE || isYotpoEnabled) {
    const rating = ratings.rating;
    return {
      ...rating,
      // For meter component we will need value in proportion of 100%
      //  so to calculate and display meter value properly, calculate % value here
      //  and pass it to the meter component
      fit: isYotpoEnabled ? (100 * rating.fit) / MAX_RATING_VALUE : rating.fit,
    };
  }

  const styleColourRatings = (ratings.styleColours || []).find(
    ({ code }) => code === styleColourCode
  );

  return styleColourRatings && styleColourRatings.rating
    ? styleColourRatings.rating
    : // Style colour ratings not available for some reason.
      // Ensure sensible defaults are set.
      {
        fit: null,
        style: null,
        total: 0,
        trueToSize: null,
        value: null,
        // this is added for the yotpo custom questions integration
        // Once the integration is done and live we ca remove `trueToSize` field
        comfort: null,
      };
};

/**
 * @param {Object} styleColour
 * @returns {Array}
 */
const mapFilterOptions = styleColour =>
  styleColour.availableColours.map(({ name, styleColourCode }) => ({
    label: name,
    value: styleColourCode,
  }));

const mapStateToProps = ({
  app: {
    config: { yotpo },
    routing: { query },
  },
  browser: { deviceIs },
  product,
  productReviews,
}) => {
  const { data } = product.styleColour;
  const queryLimit = query.limit && parseInt(query.limit, 10);
  const isYotpoEnabled = yotpo.enabled;

  return {
    ...productReviews,
    filterOptions: mapFilterOptions(data),
    isMobile: deviceIs.mobile,
    limit:
      queryLimit || (deviceIs.mobile ? DEFAULT_MOBILE_LIMIT : DEFAULT_LIMIT),
    linkType: 'to',
    rating: mapRating(data.ratings, productReviews.sortBy, isYotpoEnabled),
    reviews: productReviews.reviews,
    styleName: data.styleName,
    styleCode: data.styleCode,
    isYotpoEnabled,
  };
};

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(
    { filter, apiFetchReviews, apiFetchAllReviews },
    dispatch
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductReviews);
